import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";

//importing custom modules
import { Button } from "../../components";
import { newUserSchema } from "../../yup";
import { getErrorMessage } from "../../utils/common";

// importing styles
import style from "./courier.module.css";
import { colors } from "../../theme/colors";

// importing api
import { useCreateUserMutation } from "../../services/api";
import { LabelBox } from "../../components";
import { RootState, useAppSelector } from "../../store";
import { SingleSelectInput } from "../../components/SelectInput";
import { Prefix } from "../../utils/constants";
import { Input } from "../../components/UserInput";

type AddCourierLocationState = {
  userRole?: number;
};

/**
 * Safely extracts courier creation state from router location.
 *
 * Args:
 *   state: Router location state value.
 *
 * Returns:
 *   A validated subset of the state or null when invalid.
 */
const getAddCourierLocationState = (
  state: unknown
): AddCourierLocationState | null => {
  if (!state || typeof state !== "object") {
    return null;
  }
  if (!("userRole" in state)) {
    return null;
  }
  const { userRole } = state as { userRole?: unknown };
  return typeof userRole === "number" || typeof userRole === "undefined"
    ? { userRole }
    : null;
};

/**
 * Safely reads the optional laboratory id from router state.
 *
 * Args:
 *   state: Router location state value.
 *
 * Returns:
 *   The laboratory id when present and numeric; otherwise undefined.
 */
const getLaboratoryIdFromLocationState = (state: unknown): number | undefined => {
  if (!state || typeof state !== "object") {
    return undefined;
  }
  if (!("laboratoryId" in state)) {
    return undefined;
  }
  const { laboratoryId } = state as { laboratoryId?: unknown };
  return typeof laboratoryId === "number" ? laboratoryId : undefined;
};

type CourierFormValues = typeof newUserSchema.__outputType;

/**
 * Renders the create-courier form and submits a new courier user.
 *
 * Notes:
 * - Requires location state to include a `userRole` id for the new courier.
 * - Assumes current user has a health organisation id in auth state.
 * - TODO: Confirm required fields and server validation rules for user creation.
 *
 * Returns:
 *   The add-courier page component.
 */
const AddCourier = () => {
  const navigation = useNavigate();
  const [createUser] = useCreateUserMutation();
  const { userDetails } = useAppSelector((state: RootState) => state.auth);
  const location = useLocation();
  const locationState = getAddCourierLocationState(location.state);
  const userRole = locationState?.userRole;
  const selectedLaboratoryId = getLaboratoryIdFromLocationState(location.state);

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CourierFormValues>({
    resolver: yupResolver(newUserSchema),
    mode: "onChange",
  });

  // Creating an courier requires auth context and a role from navigation state.
  /**
   * Submits a new courier user to the API.
   *
   * Args:
   *   values: Form values validated by the courier schema.
   *
   * Returns:
   *   A promise that resolves when the creation completes.
   */
  const onSubmit = async (values: CourierFormValues) => {
    try {
      setLoading(true);

      // Prevent creating an orphaned courier when auth or role context is missing.
      if ((!userDetails && !selectedLaboratoryId) || !userRole) {
        setMessage({
          type: "error",
          text: "Missing user context. Please ensure you're logged in and try again.",
        });
        return;
      }
      const laboratoryId =
        userDetails?.laboratory_id ?? selectedLaboratoryId ?? 0;
      const payload = {
        prefix: values.prefix,
        given_name: values.given_name,
        surname: values.surname,
        phone: values.phone ?? "",
        mail: values.mail,
        laboratory_id: laboratoryId,
        user_role_id: userRole,
      };

      await createUser(payload).unwrap();
      setLoading(false);
      setMessage({
        type: "success",
        text: "Courier added successfully.",
      });
      // Leave a short delay so users can see the success confirmation.
      // Allow time for the success message to be visible before navigating back.
      setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography className={style.detailHeading}>Add New Courier</Typography>
        <Typography className={style.description}>
          Enter the below details to start adding a new courier to Pegasus
          Cybersoft.
        </Typography>
        <Box className={style.flexTop}>
          <Box className={style.row}>
            <SingleSelectInput
              placeholder="Select Prefix"
              label="Prefix"
              register={register}
              name="prefix"
              errors={errors}
              data={Prefix}
            />
          </Box>

          <Box className={style.row}>
            <Input
              placeholder="First Name"
              label="First Name"
              required
              register={register}
              name="given_name"
              errors={errors}
            />
            <Input
              placeholder="Last Name"
              label="Last Name"
              required
              register={register}
              name="surname"
              errors={errors}
            />
          </Box>

          <Box className={style.row}>
            <Input
              placeholder="Email Address"
              label="Email Address"
              required
              register={register}
              name="mail"
              errors={errors}
            />
            <Input
              placeholder="Phone Number"
              label="Phone Number"
              register={register}
              name="phone"
              errors={errors}
            />
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label="Cancel"
            />
            <Button
              loading={loading}
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label="Save"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddCourier;
